import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "prompt-engineering",
  name: "プロンプトエンジニアリング",
  nameEn: "Prompt Engineering",
  seo: {
    title:
      "プロンプトエンジニアリングを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "Few-shot・Chain-of-Thought・プロンプトテンプレート設計から評価手法まで、プロンプトエンジニアリングをゆめさくエンジニアで実践的に学びましょう。AI時代の新しい必須スキルです。",
    url: "/columns/prompt-engineering-programming-school",
  },
  hero: {
    title: "プロンプトエンジニアリングを学べる女性向けプログラミングスクール",
    englishTitle: "Learn Prompt Engineering",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "プロンプトエンジニアリング" },
  ],
  overview: {
    heading: "プロンプトエンジニアリングとは？",
    paragraphs: [
      "プロンプトエンジニアリングとは、ChatGPTなどの大規模言語モデル（LLM）から望む出力を引き出すために、入力（プロンプト）を体系的に設計・最適化する技術です。",
      "Few-shot学習、Chain-of-Thought（思考の連鎖）、プロンプトテンプレート設計など、さまざまなテクニックを駆使して、AIの出力品質を大幅に向上させることができます。単にAIに質問するだけでなく、ビジネス要件に合った高精度な出力を安定的に得るためのスキルです。",
      "プログラミングの知識がなくても始められる一方、Python等と組み合わせることでAPI連携やワークフロー自動化など、より高度な活用が可能になります。",
    ],
    useCases: [
      {
        label: "業務自動化",
        description:
          "メール作成、報告書のドラフト、議事録要約など、日常業務をLLMで効率化するプロンプトを設計します。",
      },
      {
        label: "コンテンツ生成",
        description:
          "マーケティングコピー、ブログ記事、SNS投稿など、一貫性のある高品質なコンテンツをAIで生成します。",
      },
      {
        label: "データ抽出・分析",
        description:
          "非構造化テキストから必要な情報を抽出したり、データの傾向分析をLLMに行わせるプロンプトを設計します。",
      },
      {
        label: "AIアプリケーション開発",
        description:
          "チャットボット、RAGシステム、エージェントなど、LLMを組み込んだアプリケーションのプロンプト設計を行います。",
      },
    ],
  },
  whyLearn: {
    heading: "プロンプトエンジニアリングを学ぶべき3つの理由",
    items: [
      {
        title: "AI時代に最も求められる新しいスキル",
        description:
          "ChatGPTの普及により、LLMを効果的に活用できるスキルはあらゆる職種で求められています。プロンプトエンジニアリングは、エンジニアだけでなくビジネス職にとっても今後の必須スキルとなります。",
      },
      {
        title: "プログラミング未経験でも始められる",
        description:
          "プロンプトエンジニアリングは自然言語（日本語・英語）でAIとやり取りするため、プログラミング経験がなくても始められます。学びながらPythonと組み合わせることで、さらに高度な活用が可能になります。",
      },
      {
        title: "急成長する専門職としてのキャリア",
        description:
          "プロンプトエンジニアは新しい職種ながら高い年収水準が提示されており、AI活用コンサルタントなどの関連職種も含めて今後さらに需要が拡大すると予測されています。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでプロンプトエンジニアリングを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、Phase 4以降の上級カリキュラムでプロンプトエンジニアリングを体系的に学びます。基本的なプロンプト設計から、Few-shot・Chain-of-Thought・自己整合性などの高度なテクニックまで段階的に習得します。",
      "実際のビジネス課題を題材にしたプロンプト設計演習や、LLM APIを活用したアプリケーション開発プロジェクトを通じて、実践的なスキルを身につけます。",
    ],
    topics: [
      "プロンプト設計の基本原則",
      "Few-shot学習・Zero-shot学習",
      "Chain-of-Thought（CoT）プロンプティング",
      "プロンプトテンプレート設計とバージョン管理",
      "出力品質の評価手法・メトリクス",
      "LLM API（OpenAI・Claude）の活用",
      "実践プロジェクト：AIチャットボット・業務自動化システムの構築",
    ],
  },
  careerPaths: {
    heading: "プロンプトエンジニアリングを活かせるキャリア",
    items: [
      {
        title: "プロンプトエンジニア",
        description:
          "LLMの出力品質を最大化するプロンプトの設計・最適化を専門とする職種です。AIプロダクトの開発チームで、モデルの性能を引き出す重要な役割を担います。",
        salary: "500〜900万円",
      },
      {
        title: "AI活用コンサルタント",
        description:
          "企業のビジネス課題に対してAI・LLMの活用戦略を提案し、導入を支援する職種です。プロンプト設計のノウハウとビジネス理解を組み合わせて価値を提供します。",
        salary: "500〜850万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "3〜6ヶ月（情報が散在し体系化が困難）",
        luagate: "2〜3ヶ月（体系的なカリキュラムで効率的に習得）",
      },
      {
        label: "挫折率",
        selfStudy: "「何が良いプロンプトか」の基準が不明確で迷走しやすい",
        luagate: "評価メトリクスとフィードバックで着実にスキルアップ",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "ChatGPTで遊ぶだけで終わりがち",
        luagate: "ビジネス課題を題材にしたプロンプト設計プロジェクト",
      },
      {
        label: "質問対応",
        selfStudy: "プロンプトの改善方法がわからない",
        luagate: "メンターがプロンプトの改善ポイントを具体的にアドバイス",
      },
      {
        label: "就職サポート",
        selfStudy: "新しい職種のため求人の探し方がわからない",
        luagate: "AI企業・DX推進企業とのネットワークを活かした就職支援",
      },
    ],
  },
  faq: [
    {
      question:
        "プロンプトエンジニアリングにプログラミングスキルは必要ですか？",
      answer:
        "基礎的なプロンプト設計はプログラミング不要で始められます。ただし、API連携やワークフロー自動化などの高度な活用にはPythonの知識があると有利です。ゆめさくエンジニアではPythonの基礎も合わせて学べます。",
    },
    {
      question: "プロンプトエンジニアの求人は実際にありますか？",
      answer:
        "急速に増加しています。「プロンプトエンジニア」という専門職だけでなく、AI活用企画・LLMアプリケーション開発・AIコンサルタントなど、プロンプトエンジニアリングスキルが求められる職種は多岐にわたります。",
    },
    {
      question: "ChatGPTを日常的に使っていますが、それだけでは不十分ですか？",
      answer:
        "日常利用の経験は良いスタートですが、プロンプトエンジニアリングは体系的な技法（Few-shot、CoTなど）や評価手法を含む専門スキルです。直感的な利用から一歩進んで、再現性のある高品質な出力を得るための技術を学びましょう。",
    },
    {
      question: "AI技術の進化でプロンプトエンジニアリングは不要になりますか？",
      answer:
        "AIが進化してもプロンプト設計の重要性は変わりません。むしろモデルが高性能になるほど、適切なプロンプトで引き出せる価値の差は大きくなります。また、プロンプトエンジニアリングで培った論理的思考力はAI以外の場面でも活かせます。",
    },
  ],
  relatedLanguages: [
    { slug: "generative-ai", name: "生成AI" },
    { slug: "rag-llm", name: "RAG・LLM" },
    { slug: "nocode-ai", name: "ノーコードAI" },
    { slug: "python", name: "Python" },
  ],
};
